import { api, type QueryParams } from '@/lib/api/client';
import type {
  ContactDirectoryEntry,
  Country,
  I18n,
  NavItem,
  PageCopyNamespace,
  PageCopyUsages,
  Paginated,
  Redirect,
  SettingsDocument,
  UiString,
  WriteWarning,
} from '@/lib/api/types';

/** A single page-copy namespace summary as returned by the list endpoint (spec §15.2). */
export interface PageCopySummary {
  namespace: string;
  ar_complete: boolean;
  updated_at: string;
  /** How many pages consume this namespace (reverse index). `0` ⇒ orphaned/unused. */
  used_by_count?: number;
}

/** Body shape for creating/updating a navigation item (label.en required). */
export interface NavItemBody {
  parent_id?: string | null;
  label: I18n;
  link_type: 'internal' | 'external';
  target_slug: string;
  url: string;
  open_in_new_tab: boolean;
  sort_order: number;
  is_visible: boolean;
}

export interface RedirectBody {
  from_path: string;
  to_path: string;
  status: 301 | 302 | 307 | 308;
  is_active?: boolean;
}

export interface ContactBody {
  label: I18n;
  email: string;
  phone?: string;
  sla?: string;
  is_visible: boolean;
  sort_order: number;
}

export interface CountryBody {
  is_default?: boolean;
  sort_order?: number;
}

/**
 * Body for `PATCH /admin/page-copy/{namespace}` — RFC 7396 JSON Merge Patch (docs
 * management.md §"merge semantics"). Send only what changes: nested objects merge key by
 * key, a JSON `null` deletes a key, scalars/arrays replace wholesale. Provide at least one
 * of `content` / `ar_complete`. PATCH never creates a namespace (404 if missing) — the admin
 * console only edits the copy of namespaces the site's authors have already defined.
 */
export interface PageCopyPatchBody {
  content?: Record<string, unknown> | null;
  ar_complete?: boolean;
}

// ── System email templates (PHASE 10/15) ──────────────────────────────────────

/** The five block types an override may use. There is no HTML block, by design. */
export type EmailBlockType = 'heading' | 'paragraph' | 'list' | 'button' | 'divider';

/** One block of a template body (`internal/email/blocks.go`). */
export interface EmailBlock {
  type: EmailBlockType;
  /** heading / paragraph prose, and a button's label. Plain text; newlines become breaks. */
  text?: string;
  /** list entries. */
  items?: string[];
  /** button destination: https://, mailto:, or a URL merge field. */
  href?: string;
}

export interface Bilingual<T> {
  en: T;
  ar: T;
}

/** A row of `GET /admin/email-templates`: one registry key, overridden or not. */
export interface EmailTemplateSummary {
  key: string;
  classification: string;
  label: string;
  description: string;
  /** What an override of this key may use as `{{namespace.field}}`. */
  merge_fields: string[];
  overridden: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

/** A saved override. */
export interface EmailTemplateOverride {
  key: string;
  classification: string;
  subject: Bilingual<string>;
  body: Bilingual<EmailBlock[]>;
  updated_by: string | null;
  updated_at: string;
}

/** One rendered locale (preview / shipped default). */
export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

/** `GET /admin/email-templates/{key}`. */
export interface EmailTemplateDetail extends EmailTemplateSummary {
  effective: 'override' | 'shipped';
  override: EmailTemplateOverride | null;
  shipped: Bilingual<RenderedEmail>;
}

/** Body of PATCH (create-or-replace) and of a draft preview. */
export interface EmailTemplateInput {
  subject: Bilingual<string>;
  body: Bilingual<EmailBlock[]>;
}

// ── Lead retention (PHASE 09/15) ──────────────────────────────────────────────

/** `GET /admin/retention/dry-run` / `POST /admin/retention/confirm` (`domain/retention.DryRun`). */
export interface RetentionDryRun {
  id: string;
  /** What a confirmation authorises: the four lead tables plus the orphan-CV sweep. */
  scope: string[];
  /** Due count per scope entry, plus the informational `application_cvs_attached` and `outbox_blank_due`. */
  counts: Record<string, number>;
  /** Sum of the scope counts; the number the admin must type. */
  total: number;
  created_by: string | null;
  /** The deletion cutoff. */
  created_at: string;
  expires_at: string;
  confirmed_by: string | null;
  confirmed_at: string | null;
  /** `false` while the backend kill switch (`RETENTION_DRY_RUN=true`) is on. */
  deletion_enabled: boolean;
}

/** Query keys for the settings tabs added in PHASE 15 (kept under the `settings` root). */
export const settingsKeys = {
  emailTemplates: () => ['settings', 'email-templates'] as const,
  emailTemplate: (key: string) => ['settings', 'email-templates', key] as const,
  retention: () => ['settings', 'retention', 'dry-run'] as const,
};

/**
 * Single client for the whole Site Settings & Chrome surface (spec §15.2, docs management.md).
 * Every write is audited and triggers an ISR revalidation of the public layout shells. Writes
 * are gated server-side by `settings:manage`, except page copy: reads need `content:read`,
 * PUT/PATCH `content:write`, and only DELETE keeps `settings:manage`.
 */
export const settingsApi = {
  // ── Settings document (field-level read-modify-write) ──────────────────────
  getSettings(): Promise<SettingsDocument> {
    return api.get<SettingsDocument>('/admin/settings');
  },
  // Accepts a PARTIAL document: the backend merges it onto the current singleton,
  // so callers send only what changed. Returns the full, updated document.
  putSettings(changes: Partial<SettingsDocument>): Promise<SettingsDocument> {
    return api.put<SettingsDocument>('/admin/settings', changes);
  },
  /** Like putSettings, keeping the `meta.warnings` a brand-asset change may carry. */
  async putSettingsWithWarnings(
    changes: Partial<SettingsDocument>,
  ): Promise<{ doc: SettingsDocument; warnings: WriteWarning[] }> {
    const res = await api.putRaw<SettingsDocument>('/admin/settings', changes);
    return { doc: res.data, warnings: res.meta?.warnings ?? [] };
  },

  // ── UI strings ─────────────────────────────────────────────────────────────
  listStrings(): Promise<UiString[]> {
    return api.get<UiString[]>('/admin/strings');
  },
  putString(namespace: string, key: string, value: I18n): Promise<UiString> {
    return api.put<UiString>(
      `/admin/strings/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
      { value },
    );
  },
  deleteString(namespace: string, key: string): Promise<null> {
    return api.del(`/admin/strings/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`);
  },

  // ── Navigation ───────────────────────────────────────────────────────────────
  getNavigation(key: string): Promise<NavItem[]> {
    return api.get<NavItem[]>(`/admin/navigation/${encodeURIComponent(key)}`);
  },
  addNavItem(key: string, body: NavItemBody): Promise<NavItem> {
    return api.post<NavItem>(`/admin/navigation/${encodeURIComponent(key)}/items`, body);
  },
  updateNavItem(id: string, body: Partial<NavItemBody>): Promise<NavItem> {
    return api.patch<NavItem>(`/admin/navigation/items/${id}`, body);
  },
  deleteNavItem(id: string): Promise<null> {
    return api.del(`/admin/navigation/items/${id}`);
  },
  reorderNav(key: string, ids: string[]): Promise<NavItem[]> {
    return api.post<NavItem[]>(`/admin/navigation/${encodeURIComponent(key)}/reorder`, { ids });
  },

  // ── Redirects ────────────────────────────────────────────────────────────────
  async listRedirects(params?: QueryParams): Promise<Paginated<Redirect>> {
    const res = await api.getRaw<Redirect[]>('/admin/redirects', params);
    return { data: res.data ?? [], meta: res.meta as Paginated<Redirect>['meta'] };
  },
  upsertRedirect(body: RedirectBody): Promise<Redirect> {
    return api.post<Redirect>('/admin/redirects', body);
  },
  deleteRedirect(id: string): Promise<null> {
    return api.del(`/admin/redirects/${id}`);
  },

  // ── Contact directory ──────────────────────────────────────────────────────
  listContactDirectory(): Promise<ContactDirectoryEntry[]> {
    return api.get<ContactDirectoryEntry[]>('/admin/contact-directory');
  },
  putContact(key: string, body: ContactBody): Promise<ContactDirectoryEntry> {
    return api.put<ContactDirectoryEntry>(
      `/admin/contact-directory/${encodeURIComponent(key)}`,
      body,
    );
  },
  deleteContact(key: string): Promise<null> {
    return api.del(`/admin/contact-directory/${encodeURIComponent(key)}`);
  },

  // ── Countries register ───────────────────────────────────────────────────────
  listCountries(): Promise<Country[]> {
    return api.get<Country[]>('/admin/countries');
  },
  putCountry(code: string, body: CountryBody): Promise<Country> {
    return api.put<Country>(`/admin/countries/${encodeURIComponent(code)}`, body);
  },
  setDefaultCountry(code: string): Promise<Country> {
    return api.post<Country>(`/admin/countries/${encodeURIComponent(code)}/default`);
  },
  deleteCountry(code: string): Promise<null> {
    return api.del(`/admin/countries/${encodeURIComponent(code)}`);
  },

  // ── Page copy ────────────────────────────────────────────────────────────────
  listPageCopy(): Promise<PageCopySummary[]> {
    return api.get<PageCopySummary[]>('/admin/page-copy');
  },
  getPageCopy(namespace: string): Promise<PageCopyNamespace> {
    return api.get<PageCopyNamespace>(`/admin/page-copy/${encodeURIComponent(namespace)}`);
  },
  /** Pages that consume this namespace (reverse index). 404 if the namespace is unknown. */
  pageCopyUsages(namespace: string): Promise<PageCopyUsages> {
    return api.get<PageCopyUsages>(`/admin/page-copy/${encodeURIComponent(namespace)}/usages`);
  },
  /**
   * Deletes a namespace (needs `settings:manage`). 409 `in_use` while any page still lists it;
   * the console names those pages from the usages index.
   */
  deletePageCopy(namespace: string): Promise<null> {
    return api.del(`/admin/page-copy/${encodeURIComponent(namespace)}`);
  },
  patchPageCopy(namespace: string, body: PageCopyPatchBody): Promise<PageCopyNamespace> {
    return api.patch<PageCopyNamespace>(`/admin/page-copy/${encodeURIComponent(namespace)}`, body);
  },
  // ── System email templates ───────────────────────────────────────────────────
  // Reads and previews need `settings:manage`; writes also need an interactive session
  // (an API token gets 403 `interactive_required`).
  listEmailTemplates(): Promise<EmailTemplateSummary[]> {
    return api.get<EmailTemplateSummary[]>('/admin/email-templates');
  },
  getEmailTemplate(key: string): Promise<EmailTemplateDetail> {
    return api.get<EmailTemplateDetail>(`/admin/email-templates/${encodeURIComponent(key)}`);
  },
  /** Create-or-replace the override (the whole document, both languages). */
  saveEmailTemplate(key: string, body: EmailTemplateInput): Promise<EmailTemplateOverride> {
    return api.patch<EmailTemplateOverride>(`/admin/email-templates/${encodeURIComponent(key)}`, body);
  },
  /** Removes the override; the next send uses the shipped template. */
  restoreEmailTemplate(key: string): Promise<null> {
    return api.del(`/admin/email-templates/${encodeURIComponent(key)}`);
  },
  /** Renders a draft (or, with no body, the effective template) with synthetic sample data. */
  previewEmailTemplate(key: string, body?: EmailTemplateInput): Promise<Bilingual<RenderedEmail>> {
    return api.post<Bilingual<RenderedEmail>>(
      `/admin/email-templates/${encodeURIComponent(key)}/preview`,
      body,
    );
  },

  // ── Lead retention ───────────────────────────────────────────────────────────
  /**
   * Computes what retention would delete as of now. Every call RECORDS a run and an audit
   * row, so the tab never refetches it on its own — only when asked to.
   */
  retentionDryRun(): Promise<RetentionDryRun> {
    return api.get<RetentionDryRun>('/admin/retention/dry-run');
  },
  /** Admin + `settings:manage` + interactive. Authorises the worker; deletes nothing itself. */
  confirmRetention(dryRunId: string, typedTotal: number): Promise<RetentionDryRun> {
    return api.post<RetentionDryRun>('/admin/retention/confirm', {
      dry_run_id: dryRunId,
      typed_total: typedTotal,
    });
  },
};
